// Ported from database-creator-main@e533337 frontend/src/wizard/StatsPanel.jsx
// Changes: import paths (../../components/Shared); text colours raised to WCAG AA contrast
// (#9ca3af -> #6b7280 on white, tile labels #6b7280 -> #4b5563 on G_BG, a selected table's row count G_DARK on G_BG; axe-core, 2026-10-04).
// Otherwise verbatim — keep diffable.
import { useState } from "react";
import { G, G_DARK, G_BG } from "../../components/Shared";

function fmtNum(n) {
  if (n === undefined || n === null) return "–";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function StatTile({ label, value }) {
  return (
    <div style={{ padding: "12px 16px", background: G_BG, border: "1px solid rgba(0,150,64,0.25)", borderRadius: 10, minWidth: 110 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: G_DARK, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Buckets are pre-aggregated server-side (numpy.histogram, capped at a fixed
// bin count) — rendering never depends on how many rows the column actually
// had, so this is just as cheap for a 5-row table as a 50,000-row one.
function Histogram({ buckets }) {
  if (!buckets || buckets.length === 0) {
    return <p style={{ fontSize: 12, color: "#6b7280" }}>No numeric values.</p>;
  }
  const max = Math.max(...buckets.map(b => b.count), 1);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
        {buckets.map((b, i) => (
          <div
            key={i}
            title={`${fmtNum(b.range_start)}–${fmtNum(b.range_end)}: ${b.count}`}
            style={{
              flex: 1, height: `${Math.max((b.count / max) * 100, b.count > 0 ? 3 : 0)}%`,
              background: G, borderRadius: "3px 3px 0 0",
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#6b7280", marginTop: 4 }}>
        <span>{fmtNum(buckets[0].range_start)}</span>
        <span>{fmtNum(buckets[buckets.length - 1].range_end)}</span>
      </div>
    </div>
  );
}

function TopValues({ items, distinctCount }) {
  if (!items || items.length === 0) {
    return <p style={{ fontSize: 12, color: "#6b7280" }}>No values.</p>;
  }
  const max = Math.max(...items.map(i => i.count), 1);
  const remaining = distinctCount - items.length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, alignItems: "center", fontSize: 12 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.value}>{it.value}</span>
          <div style={{ background: "#f0fdf4", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${(it.count / max) * 100}%`, background: G, height: 10, borderRadius: 4 }} />
          </div>
          <span style={{ color: "#6b7280", fontFamily: "'JetBrains Mono', monospace" }}>{it.count}</span>
        </div>
      ))}
      {remaining > 0 && (
        <span style={{ fontSize: 11, color: "#6b7280" }}>+ {remaining} more distinct value{remaining > 1 ? "s" : ""}</span>
      )}
    </div>
  );
}

function ColumnStatCard({ name, stat }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{name}</strong>
        <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
          {stat.null_count} empty · {stat.distinct_count} distinct
        </span>
      </div>
      <div style={{ marginTop: 10 }}>
        {stat.kind === "numeric" && (
          <>
            <Histogram buckets={stat.histogram} />
            {stat.histogram.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11, color: "#6b7280", marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                <span>min {fmtNum(stat.min)}</span>
                <span>max {fmtNum(stat.max)}</span>
                <span>mean {fmtNum(stat.mean)}</span>
                <span>median {fmtNum(stat.median)}</span>
              </div>
            )}
          </>
        )}
        {stat.kind === "categorical" && <TopValues items={stat.top_values} distinctCount={stat.distinct_count} />}
        {stat.kind === "date" && (
          <p style={{ fontSize: 12, color: "#374151", margin: 0 }}>
            {stat.min ? <>range: {stat.min} → {stat.max}</> : "No values."}
          </p>
        )}
        {stat.kind === "other" && <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No distribution shown for this type.</p>}
      </div>
    </div>
  );
}

export default function StatsPanel({ stats }) {
  const tableNames = Object.keys(stats.tables || {});
  const [selected, setSelected] = useState(tableNames[0] || null);

  if (tableNames.length === 0) {
    return <p style={{ fontSize: 13, color: "#6b7280" }}>No data to summarize.</p>;
  }

  const table = stats.tables[selected] || stats.tables[tableNames[0]];

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <StatTile label="Tables" value={stats.total_tables} />
        <StatTile label="Total rows" value={stats.total_rows.toLocaleString()} />
        <StatTile label="Primary keys" value={stats.primary_key_count} />
        <StatTile label="Foreign keys" value={stats.foreign_key_count} />
      </div>

      {tableNames.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {tableNames.map(name => {
            const isSelected = name === selected;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setSelected(name)}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                  fontFamily: "inherit", fontWeight: isSelected ? 700 : 500, border: "1px solid",
                  background: isSelected ? G_BG : "#ffffff",
                  color: isSelected ? G_DARK : "#374151",
                  borderColor: isSelected ? G : "#d1d5db",
                }}
              >
                {name} <span style={{ color: isSelected ? G_DARK : "#6b7280" }}>({stats.tables[name].row_count.toLocaleString()})</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
        {Object.entries(table.columns).map(([name, stat]) => (
          <ColumnStatCard key={name} name={name} stat={stat} />
        ))}
      </div>
    </div>
  );
}
